<template>
    <v-stepper v-model="e1" class="sign-up-steps campaign-steps">

        <div class="modal-close-btn">
            <v-btn icon @click="$emit('hideDialog')">
                <v-icon size="18" color="grey">mdi-close</v-icon>
            </v-btn>
        </div>

        <v-stepper-header
                class="justify-start"
        >
            <v-stepper-step :complete="e1 > 1" step="1"></v-stepper-step>
            <v-stepper-step :complete="e1 > 2" step="2"></v-stepper-step>
            <v-stepper-step :complete="e1 > 3" step="3"></v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>

            <v-stepper-content step="1">
                <v-row class="justify-center">
                    <v-col cols="11" md="9" class="text-center">
                        <h3>Let's get you some money!</h3>
                        <div class="mt-2">
                            <v-row>
                                <v-col class="pb-0">
                                    <h4>What is your goal?</h4>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="pb-0 pt-2">
                                    <p>
                                        You can always change your goal amount later.
                                    </p>
                                </v-col>
                            </v-row>
                            <v-row class="goal-presets">
                                <v-col
                                    v-for="preset in presetAmounts"
                                    :key="'preset-' + preset"
                                    cols="6"
                                    sm="3"
                                    class="pa-1"
                                >
                                    <v-btn
                                        :color="selectedPreset === preset ? 'primary' : 'primary'"
                                        :outlined="selectedPreset !== preset"
                                        depressed
                                        block
                                        height="44"
                                        @click="selectPreset(preset)"
                                        :disabled="isCallingApi"
                                        class="goal-preset-btn"
                                    >
                                        ${{ preset.toLocaleString() }}
                                    </v-btn>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="pt-3">
                                    <v-text-field
                                        v-model="customAmount"
                                        outlined
                                        dense
                                        label="Or enter custom amount"
                                        type="number"
                                        min="100"
                                        max="10000"
                                        prefix="$"
                                        :disabled="isCallingApi"
                                        :error-messages="amountError"
                                        @input="onCustomAmountInput"
                                        @focus="clearPresetSelection"
                                        @blur="onCustomAmountBlur"
                                        hide-details="auto"
                                    ></v-text-field>
                                    <p class="custom-amount-hint mt-1">Min: $100 · Max: $10,000</p>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="pb-0 pt-4">
                                    <h4>Tell your story (optional)</h4>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="pb-0 pt-2">
                                    <p>
                                        Share why you're raising funds and how it will help you.
                                    </p>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col>
                                    <v-textarea
                                              outlined
                                              dense
                                              v-model="campaign.message"
                                              :disabled="isCallingApi"
                                              placeholder="I'm raising funds to help cover my housing costs while attending college..."
                                              rows="3"
                                              counter="500"
                                              maxlength="500"
                                    ></v-textarea>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="pt-0">
                                    <CampaignItemsEditor
                                        :is-itemized.sync="isItemized"
                                        :items.sync="campaignItems"
                                        :goal-amount="campaign.amount || 0"
                                        :disabled="isCallingApi"
                                        @itemsChanged="onItemsChanged"
                                    />
                                </v-col>
                            </v-row>
                            <v-row class="d-flex pt-0">
                                <v-col class="pb-0">
                                    <v-btn
                                            color="primary"
                                            class="next-btn btn-edurent"
                                            height="45"
                                            width="163"
                                            @click="validatePrice"
                                            depressed
                                    >
                                        Next
                                    </v-btn>
                                </v-col>
                            </v-row>
                            <v-row class="d-flex pb-0">
                                <v-col class="pb-0">
                                    <v-btn
                                            color="primary"
                                            text
                                            depressed
                                            class="skip-btn btn-edurent"
                                            height="45"
                                            width="163"
                                            @click="$emit('hideDialog')"
                                    >
                                        Cancel
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </div>
                    </v-col>
                </v-row>
            </v-stepper-content>

            <v-stepper-content step="2">
                <v-row class="justify-center">
                    <v-col cols="12" md="10" class="text-center">
                        <v-row>
                            <v-col class="pb-0">
                                <h1>When would you like to receive your raised funds?</h1>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col class="pb-0">
                                <p>
                                    The choice is yours, but we recommend waiting 2 weeks before your school start date to increase your chances of success in putting this money towards school-related costs!
                                </p>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col class="pb-0">
                                <v-radio-group column
                                               class="receive-funds-group"
                                               v-model="campaign.deposit_type"
                                               :error-messages="depositTypeError"
                                               @input="$v.campaign.deposit_type.$touch()"
                                               @blur="$v.campaign.deposit_type.$touch()">
                                    <v-radio value="delay" class="receive-funds">
                                        <template slot="label">
                                            <div class="receive-funds-label">
                                                <span class="option">2 weeks before my school start date</span>
                                                <span class="extra">Your funds will be deposited on {{campaignEndDate}}</span>
                                            </div>
                                        </template>
                                    </v-radio>
                                    <v-radio label="" value="immediate" class="receive-funds">
                                        <template slot="label">
                                            <div class="receive-funds-label">
                                                <span class="option">Immediately after my goal is reached</span>
                                                <span class="extra">Your funds will be deposited immediately after reaching your goal</span>
                                            </div>
                                        </template>
                                    </v-radio>
                                </v-radio-group>
                            </v-col>
                        </v-row>
                        <v-row class="d-flex pt-2">
                            <v-col class="pb-0">
                                <v-btn
                                        color="primary"
                                        class="next-btn btn-edurent"
                                        height="45"
                                        width="163"
                                        @click="save"
                                        depressed
                                        :loading="isCallingApi"
                                >
                                    Start My Campaign
                                </v-btn>
                            </v-col>
                        </v-row>
                        <v-row class="d-flex pb-0">
                            <v-col class="pb-0">
                                <v-btn
                                        color="primary"
                                        text
                                        depressed
                                        class="skip-btn btn-edurent"
                                        height="45"
                                        width="163"
                                >
                                    Cancel
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-stepper-content>

            <v-stepper-content step="3">
                <v-row class="justify-center">
                    <v-col cols="12" class="text-center">
                        <v-row>
                            <v-col class="pb-0">
                                <h1>Share your fundraiser!</h1>
                            </v-col>
                        </v-row>
                        <v-row class="d-flex justify-center">
                            <v-col class="pb-6" cols="12" md="8">
                                <p>
                                    The more you share, the easier and faster it will be to reach your goal.
                                </p>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>

                                <v-card
                                        elevation="0"
                                        color="#F4F5F6"
                                        class="pa-8 campaign-social"
                                >

                                    <v-row>
                                        <v-col class="d-flex justify-center pb-6 pt-0">
                                            <!-- SMS -->
                                            <a :href="smsShareUrl" class="share-link d-flex mr-6">
                                                <v-avatar size="40" color="#25D366" class="share-icon">
                                                    <v-icon color="white" size="22">mdi-message-text</v-icon>
                                                </v-avatar>
                                            </a>
                                            <!-- Facebook -->
                                            <a
                                                :href="facebookShareUrl"
                                                class="share-link d-flex mr-6"
                                                @click.prevent="openShareWindow(facebookShareUrl)"
                                            >
                                                <v-img height="40" width="40" contain src="@/assets/icons/campaign-facebook.png" class="share-icon"></v-img>
                                            </a>
                                            <!-- X (Twitter) -->
                                            <a
                                                :href="twitterShareUrl"
                                                class="share-link d-flex mr-6"
                                                @click.prevent="openShareWindow(twitterShareUrl)"
                                            >
                                                <v-avatar size="40" color="#000" class="share-icon">
                                                    <span class="x-logo">X</span>
                                                </v-avatar>
                                            </a>
                                            <!-- Snapchat -->
                                            <a
                                                href="#"
                                                class="share-link d-flex mr-6"
                                                v-clipboard="campaignUrl"
                                                @click.prevent="onSnapchatShare"
                                            >
                                                <v-img height="40" width="40" contain src="@/assets/icons/campaign-snapchat.svg" class="share-icon rounded-circle"></v-img>
                                            </a>
                                            <!-- Email -->
                                            <a :href="emailShareUrl" class="share-link d-flex">
                                                <v-avatar size="40" color="#EA4335" class="share-icon">
                                                    <v-icon color="white" size="20">mdi-email-outline</v-icon>
                                                </v-avatar>
                                            </a>
                                        </v-col>
                                    </v-row>

                                    <v-row class="d-flex copy-campaign">
                                        <v-col class="d-flex pb-0">
                                            <v-text-field
                                                    v-model="campaignUrl"
                                                    outlined
                                                    type="text"
                                                    dense
                                                    class="btn-edu-height"
                                                    hide-details
                                                    background-color="white"
                                            >
                                            </v-text-field>
                                            <v-btn
                                                    depressed
                                                    class="btn-edurent copy-btn"
                                                    v-clipboard="campaignUrl"
                                                    offset-y
                                                    large
                                                    flat
                                                    color="primary"
                                                    @click="copy"
                                                    height="46"
                                            >
                                                Copy
                                            </v-btn>
                                        </v-col>
                                    </v-row>

                                </v-card>

                            </v-col>
                        </v-row>
                        <v-row class="d-flex pt-2">
                            <v-col class="pb-0">
                                <v-btn
                                        color="primary"
                                        class="next-btn btn-edurent"
                                        height="45"
                                        width="220"
                                        @click="finish"
                                        depressed
                                        :loading="isCallingApi"
                                >
                                    View My Campaign
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>

            </v-stepper-content>

        </v-stepper-items>
    </v-stepper>
</template>

<script>
    import { required } from 'vuelidate/lib/validators'
    import UserService from "../services/user.service";
    import moment from "moment";
    import CampaignItemsEditor from "./campaign/CampaignItemsEditor.vue";

    export default {
        name: 'Campaign',
        components: {
            CampaignItemsEditor
        },
        validations: {
            campaign: {
                amount: { required },
                deposit_type: { required },
            }
        },
        data() {
          return {
              e1: 1,
              steps: 2,
              presetAmounts: [500, 1000, 1500, 2000],
              selectedPreset: null,
              customAmount: null,
              campaign: null,
              campaignUrl: '',
              isCallingApi: false,
              isItemized: false,
              campaignItems: []
          }
        },
        watch: {
            steps(val) {
                if (this.e1 > val) {
                    this.e1 = val
                }
            },
        },
        computed: {
            amountError() {
                const errors = [];
                if (!this.$v.campaign.amount.$dirty) return errors;
                !this.$v.campaign.amount.required && errors.push('Amount is required');
                return errors
            },
            depositTypeError() {
                const errors = [];
                if (!this.$v.campaign.deposit_type.$dirty) return errors;
                !this.$v.campaign.deposit_type.required && errors.push('Deposit is required');
                return errors
            },
            user() {
                return this.$store.getters.getUser;
            },
            campaignEndDate() {
                return moment(this.user.university_start_date).subtract(14, 'd').format('L');
            },
            smsMessage() {
                return `Hey! ${this.user.name} is raising funds for college and could use your support. Every little bit helps! Check out their campaign: ${this.campaignUrl}`
            },
            shareTitle() {
                return `Help ${this.user.name} raise funds for college`
            },
            shareDescription() {
                return `${this.user.name} is off to school soon and is raising funds to help them while at school. Help them by donating via the shared link.`
            },
            facebookShareUrl() {
                const url = encodeURIComponent(this.campaignUrl)
                const quote = encodeURIComponent(this.shareTitle)
                return `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${quote}`
            },
            twitterShareUrl() {
                const url = encodeURIComponent(this.campaignUrl)
                const text = encodeURIComponent(this.shareTitle)
                return `https://x.com/intent/tweet?url=${url}&text=${text}&hashtags=edurent`
            },
            smsShareUrl() {
                const body = encodeURIComponent(this.smsMessage)
                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
                return isIOS ? `sms:&body=${body}` : `sms:?body=${body}`
            },
            emailShareUrl() {
                const subject = encodeURIComponent(this.shareTitle)
                const body = encodeURIComponent(`${this.shareDescription}\n\n${this.campaignUrl}`)
                return `mailto:?subject=${subject}&body=${body}`
            }
        },
        methods: {
            initCampaign() {
                this.campaign = {
                    user_id: '',
                    amount: '',
                    message: '',
                    deposit_type: ''
                }
                this.selectedPreset = null
                this.customAmount = null
                this.isItemized = false
                this.campaignItems = []
            },
            onItemsChanged(validItems, itemsTotal) {
                // Store the items
                this.campaignItems = validItems

                if (this.isItemized && itemsTotal > 0) {
                    this.campaign.amount = itemsTotal
                    this.selectedPreset = null
                    this.customAmount = itemsTotal
                }
            },
            selectPreset(amount) {
                this.selectedPreset = amount
                this.customAmount = null
                this.campaign.amount = amount
                this.$v.campaign.amount.$touch()
            },
            onCustomAmountInput() {
                if (this.customAmount && parseFloat(this.customAmount) > 0) {
                    this.selectedPreset = null
                    this.campaign.amount = parseFloat(this.customAmount)
                    this.$v.campaign.amount.$touch()
                }
            },
            clearPresetSelection() {
                this.selectedPreset = null
            },
            onCustomAmountBlur() {
                // If custom amount is empty/invalid, select default preset
                if (!this.customAmount || parseFloat(this.customAmount) <= 0) {
                    this.selectPreset(this.presetAmounts[0])
                }
            },
            validatePrice() {
                // Set amount from custom if preset not selected
                if (!this.selectedPreset && this.customAmount) {
                    this.campaign.amount = parseFloat(this.customAmount)
                }

                this.$v.campaign.amount.$touch();

                if (this.$v.campaign.amount.$invalid || !this.campaign.amount) {
                    this.$store.commit('setSnackbar', {
                        show: true,
                        message: 'Please select or enter a goal amount',
                        color: 'red'
                    });
                    return;
                }

                // Validate min/max
                if (this.campaign.amount < 100) {
                    this.$store.commit('setSnackbar', {
                        show: true,
                        message: 'Minimum goal amount is $100',
                        color: 'red'
                    });
                    return;
                }

                if (this.campaign.amount > 10000) {
                    this.$store.commit('setSnackbar', {
                        show: true,
                        message: 'Maximum goal amount is $10,000',
                        color: 'red'
                    });
                    return;
                }

                this.e1 = 2;
            },
            save: async function() {
                this.$v.campaign.deposit_type.$touch();
                if (this.$v.campaign.deposit_type.$invalid) {
                    this.$store.commit('setSnackbar', {
                        show: true,
                        message: 'You must select deposit type',
                        color: 'red'
                    });

                    return;
                }

                this.campaign.user_id = this.user.id;

                // Prepare campaign data with itemized info
                const campaignData = {
                    campaign: {
                        ...this.campaign,
                        is_itemized: this.isItemized
                    }
                };

                // Add items if itemized
                if (this.isItemized && this.campaignItems.length > 0) {
                    campaignData.items = this.campaignItems
                        .filter(item => item.name && item.goal_amount > 0)
                        .map(item => ({
                            category: item.category,
                            name: item.name,
                            goal_amount: item.goal_amount
                        }));
                }

                this.isCallingApi = true;
                const campaign    = await UserService.addNewCampaign(campaignData);
                this.isCallingApi = false;

                this.campaignUrl = `${process.env.VUE_APP_CLIENT_BASE_URL}/campaign/${campaign.hash}`
                this.e1 = 3;

                this.$emit('getCampaign');
            },
            finish() {
                this.e1 = 1;
                this.initCampaign();
                this.$v.$reset();
                this.$emit('hideDialog')
            },
            copy() {
                this.$store.commit('setSnackbar', {
                    show: true,
                    message: 'Link copied to clipboard!',
                    color: 'success'
                })
            },
            onSnapchatShare() {
                this.$store.commit('setSnackbar', {
                    show: true,
                    message: 'Link copied! Share it on your Snapchat Story.',
                    color: 'success'
                })
            },
            openShareWindow(url) {
                const width = 600
                const height = 400
                const left = (window.innerWidth - width) / 2
                const top = (window.innerHeight - height) / 2
                window.open(
                    url,
                    'share',
                    `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`
                )
            }
        },
        beforeMount() {
            this.initCampaign();
        }
    }
</script>

<style>

    .campaign-steps h1 {
        font-size: 20px !important;
        font-weight: 700;
    }
    .campaign-steps h4 {
        font-size: 16px !important;
        font-weight: 700;
    }
    .campaign-steps p {
        font-size: 12px !important;
        font-weight: 400;
        font-style: italic;
    }

    .receive-funds {
        padding: 15px;
        border-radius: 4px;
        border: 1px solid #d8d8d8;
        display: flex;
        align-items: flex-start;
    }
    .receive-funds-group .receive-funds.v-item--active {
        border: 1px solid #1976d2;
    }

    .receive-funds-label {
        display: flex;
        flex-direction: column;
    }
    .receive-funds-label .option {
        font-size: 14px !important;
        font-weight: 700;
        color: #000;
    }
    .receive-funds-label .extra {
        font-size: 12px !important;
        line-height: 18px;
        font-weight: 400;
        font-style: italic;
        color: #000;
    }
    .campaign-social {
        border-radius: 10px !important;
    }
    .campaign-social .v-btn {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    }
    .btn-edurent.copy-btn .v-btn__content {
        color: #FFF !important;
    }
    .campaign-social .v-input {
        border-right: 0;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
    }

    .campaign-social .x-logo {
        color: white;
        font-size: 20px;
        font-weight: 700;
        font-family: Arial, sans-serif;
    }

    .campaign-social .share-link {
        text-decoration: none;
    }

    .campaign-social .share-icon {
        cursor: pointer;
        transition: transform 0.2s ease;
    }

    .campaign-social .share-icon:hover {
        transform: scale(1.1);
    }

    .goal-presets {
        margin: 0 -4px;
    }

    .goal-preset-btn {
        font-size: 15px !important;
        font-weight: 600;
    }

    .goal-preset-btn.v-btn--outlined {
        border: 2px solid #1976d2 !important;
    }

    .custom-amount-hint {
        font-size: 11px !important;
        color: #666;
        text-align: left;
        font-style: normal !important;
    }

</style>


